import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getExtricationSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import { saveExtrication, setUpdateInputRows } from '../store/extrication.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialExtricationState } from '../store/extrication.reducers'
import { goBack } from '../../../../utilities/navigation.util'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'

const Extrication: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const extrication = useSelector(getExtricationSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = extrication

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const dateTimeValidationCheck = initialStateCheck()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'extrication/SET_TIME', time: time })
              dispatch({ type: 'extrication/SET_DATE', date: date })
              dispatch(saveExtrication())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: extrication.key,
                rowActionType: TimelineConstants.SET_EXTRICATION_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(extrication, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialExtricationState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(null))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='ExtricationPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Extrication</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'extrication'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default Extrication
